import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Search, Play, CheckCircle2, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

/**
 * Entity duplicates diagnostic panel: Scan for entities whose normalized
 * names collide, preview a merge, then apply it.
 */
export function EntityDuplicatesPanel() {
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [previews, setPreviews] = useState({});       // keyed by normalized_name
  const [previewLoading, setPreviewLoading] = useState({});
  const [applyResults, setApplyResults] = useState({}); // keyed by normalized_name
  const [applyLoading, setApplyLoading] = useState({});

  const runScan = async () => {
    setScanning(true);
    setScanResult(null);
    setPreviews({});
    setApplyResults({});
    try {
      const { data } = await diagnosticsAPI.entityDuplicatesScan();
      setScanResult(data);
      if (data.collisions?.length === 0) {
        toast.success('No duplicate entities found');
      } else {
        toast.success(`Found ${data.collisions?.length} collision(s)`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setScanning(false);
    }
  };

  const runPreview = async (collision) => {
    const key = collision.normalized_name;
    setPreviewLoading(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await diagnosticsAPI.entityDuplicatesMergePreview(
        collision.recommended_survivor,
        collision.recommended_retire[0],
      );
      setPreviews(prev => ({ ...prev, [key]: data }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const runApply = async (collision) => {
    const key = collision.normalized_name;
    setApplyLoading(prev => ({ ...prev, [key]: true }));
    try {
      const { data } = await diagnosticsAPI.entityDuplicatesMergeApply(
        collision.recommended_survivor,
        collision.recommended_retire[0],
      );
      setApplyResults(prev => ({ ...prev, [key]: data }));
      setPreviews(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      toast.success(`Merge applied: ${data.total_modified} records reassigned`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Entity Duplicates</CardTitle>
        <p className="text-xs text-muted-foreground">
          Scan for entities whose normalized names collide (&amp; vs and, case, accents).
          Preview and merge duplicates.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Scan control */}
        <div className="flex gap-2">
          <Button onClick={runScan} disabled={scanning} variant="outline" size="sm">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Scan for Duplicates
          </Button>
        </div>

        {/* Scan results */}
        {scanResult && (
          <div className="space-y-3">
            <div className="text-sm">
              Total collisions: <strong>{scanResult.collisions?.length ?? 0}</strong>
            </div>

            {scanResult.collisions?.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" /> No duplicate entities detected.
              </div>
            )}

            {scanResult.collisions?.map((collision, ci) => {
              const key = collision.normalized_name;
              const preview = previews[key];
              const applyResult = applyResults[key];
              const isPreviewLoading = previewLoading[key];
              const isApplyLoading = applyLoading[key];

              return (
                <div key={ci} className="border rounded p-3 space-y-2">
                  {/* Collision header */}
                  <div className="text-sm font-medium">
                    Collision: <Badge variant="outline">{collision.normalized_name}</Badge>
                  </div>

                  {/* Entities in this collision */}
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1.5 font-medium">ID</th>
                          <th className="text-left px-2 py-1.5 font-medium">Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Type</th>
                          <th className="text-right px-2 py-1.5 font-medium">Accounts</th>
                          <th className="text-left px-2 py-1.5 font-medium">Canonical</th>
                        </tr>
                      </thead>
                      <tbody>
                        {collision.entities?.map((ent, ei) => (
                          <tr key={ei} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="px-2 py-1.5 font-mono">{ent.id}</td>
                            <td className="px-2 py-1.5">{ent.name}</td>
                            <td className="px-2 py-1.5">{ent.type}</td>
                            <td className="px-2 py-1.5 text-right">{ent.account_count}</td>
                            <td className="px-2 py-1.5">
                              {ent.is_canonical ? (
                                <Badge variant="outline" className="text-green-600 border-green-300">yes</Badge>
                              ) : (
                                <Badge variant="outline" className="text-red-600 border-red-300">no</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Recommended merge */}
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>
                      Survivor: <Badge variant="outline" className="text-green-600 border-green-300">{collision.recommended_survivor}</Badge>
                    </span>
                    <span>
                      Retire: <Badge variant="outline" className="text-red-600 border-red-300">{collision.recommended_retire?.[0]}</Badge>
                    </span>
                  </div>

                  {/* Preview / Apply buttons */}
                  <div className="flex gap-2">
                    {!preview && !applyResult && (
                      <Button onClick={() => runPreview(collision)} disabled={isPreviewLoading} variant="outline" size="sm">
                        {isPreviewLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                        Preview Merge
                      </Button>
                    )}
                    {preview && preview.total_references > 0 && (
                      <Button onClick={() => runApply(collision)} disabled={isApplyLoading} size="sm" variant="destructive">
                        {isApplyLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                        Apply Merge ({preview.total_references} references)
                      </Button>
                    )}
                  </div>

                  {/* Preview results */}
                  {preview && (
                    <div className="space-y-2">
                      <div className="text-sm">
                        Total references: <strong>{preview.total_references}</strong> across{' '}
                        <strong>{preview.affected_collections}</strong> collection/field pairs
                      </div>

                      {preview.total_references === 0 && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle2 className="h-4 w-4" /> No references to retired entity found. Already merged or never used.
                        </div>
                      )}

                      {preview.scan_results?.filter(r => r.count > 0).length > 0 && (
                        <div className="border rounded overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b bg-muted/50">
                                <th className="text-left px-2 py-1.5 font-medium">Collection</th>
                                <th className="text-left px-2 py-1.5 font-medium">Field</th>
                                <th className="text-left px-2 py-1.5 font-medium">Match Type</th>
                                <th className="text-right px-2 py-1.5 font-medium">Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.scan_results.filter(r => r.count > 0).map((r, i) => (
                                <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                                  <td className="px-2 py-1.5">{r.collection}</td>
                                  <td className="px-2 py-1.5 font-mono">{r.field}</td>
                                  <td className="px-2 py-1.5">{r.match_type}</td>
                                  <td className="px-2 py-1.5 text-right font-semibold">{r.count}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Apply result */}
                  {applyResult && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        Merge applied: {applyResult.total_modified} records reassigned
                      </div>

                      {applyResult.reassignments?.length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground">
                            Reassignment details ({applyResult.reassignments.length} operations)
                          </summary>
                          <div className="border rounded mt-1 overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-muted/50">
                                  <th className="text-left px-2 py-1 font-medium">Collection</th>
                                  <th className="text-left px-2 py-1 font-medium">Field</th>
                                  <th className="text-right px-2 py-1 font-medium">Matched</th>
                                  <th className="text-right px-2 py-1 font-medium">Modified</th>
                                </tr>
                              </thead>
                              <tbody>
                                {applyResult.reassignments.map((r, i) => (
                                  <tr key={i} className="border-b last:border-0">
                                    <td className="px-2 py-1">{r.collection}</td>
                                    <td className="px-2 py-1 font-mono">{r.field}</td>
                                    <td className="px-2 py-1 text-right">{r.matched}</td>
                                    <td className="px-2 py-1 text-right font-semibold">{r.modified}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      )}

                      {applyResult.post_merge_remaining > 0 && (
                        <div className="flex items-center gap-2 text-sm text-amber-600">
                          <AlertTriangle className="h-4 w-4" />
                          Warning: {applyResult.post_merge_remaining} references still remain after merge
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
